import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Stack,
    CircularProgress,
    Tooltip,
    Alert,
} from '@mui/material';
import { Refresh, ContentCopy, Code } from '@mui/icons-material';
import { useTranslate, useNotification } from '@refinedev/core';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import axiosInstance from '../../api/axios-config';
import { TeleSurveilleurConfig } from './types';

// Register languages
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('xml', xml);

// Custom SIA DC-09 syntax highlighter
const SiaHighlighter: React.FC<{ content: string }> = ({ content }) => {
    // Parse and colorize complete SIA DC-09 format
    // Example: 9E71006B"SIA-DCS"0001L0#PTI04024[#PTI04024|NHA^GPS43.28/5.40#NAME=Jean^]_11:44:09,01-21-2026
    const renderSia = (text: string) => {
        const parts: React.ReactNode[] = [];
        let remaining = text;
        let key = 0;

        // Colors
        const colors = {
            crc: '#ff79c6',          // Pink for CRC
            length: '#bd93f9',       // Purple for length
            header: '#f8f8f2',       // White for "SIA-DCS"
            sequence: '#8be9fd',     // Cyan for sequence number
            prefixRcvr: '#ffb86c',   // Orange for prefix+receiver (L0)
            bracket: '#c678dd',      // Purple for [ ]
            delimiter: '#e06c75',    // Red for | ^ #
            account: '#61afef',      // Blue for account number
            code: '#e5c07b',         // Yellow for alarm code
            prefix: '#98c379',       // Green for #KEY=
            value: '#abb2bf',        // Gray for values
            gps: '#56b6c2',          // Cyan for GPS coordinates
            timestamp: '#6272a4',    // Gray-blue for timestamp
        };

        // Process the string - complete SIA DC-09 message format
        while (remaining.length > 0) {
            // CRC (4 hex chars at start, before 0)
            const crcMatch = remaining.match(/^([0-9A-F]{4})(0[0-9A-F]{3})/);
            if (crcMatch && parts.length === 0) {
                parts.push(
                    <Tooltip key={key++} title="CRC-16" arrow placement="top">
                        <span style={{ color: colors.crc }}>{crcMatch[1]}</span>
                    </Tooltip>
                );
                parts.push(
                    <Tooltip key={key++} title="Longueur (hex)" arrow placement="top">
                        <span style={{ color: colors.length }}>{crcMatch[2]}</span>
                    </Tooltip>
                );
                remaining = remaining.slice(8);
                continue;
            }

            // "SIA-DCS" header
            if (remaining.startsWith('"SIA-DCS"')) {
                parts.push(
                    <Tooltip key={key++} title="Protocole SIA DC-09" arrow placement="top">
                        <span style={{ color: colors.header, fontWeight: 'bold' }}>"SIA-DCS"</span>
                    </Tooltip>
                );
                remaining = remaining.slice(9);
                continue;
            }

            // Sequence number (4 digits after SIA-DCS)
            const seqMatch = remaining.match(/^(\d{4})([A-Z])(\d)/);
            if (seqMatch) {
                parts.push(
                    <Tooltip key={key++} title="Numéro de séquence" arrow placement="top">
                        <span style={{ color: colors.sequence }}>{seqMatch[1]}</span>
                    </Tooltip>
                );
                parts.push(
                    <Tooltip key={key++} title="Préfixe compte + Récepteur" arrow placement="top">
                        <span style={{ color: colors.prefixRcvr }}>{seqMatch[2]}{seqMatch[3]}</span>
                    </Tooltip>
                );
                remaining = remaining.slice(6);
                continue;
            }

            // Account before data block: #ACCOUNT[
            const accountBeforeMatch = remaining.match(/^#([A-Z0-9]+)\[/);
            if (accountBeforeMatch) {
                parts.push(<span key={key++} style={{ color: colors.delimiter }}>#</span>);
                parts.push(
                    <Tooltip key={key++} title="Numéro de compte" arrow placement="top">
                        <span style={{ color: colors.account }}>{accountBeforeMatch[1]}</span>
                    </Tooltip>
                );
                remaining = remaining.slice(accountBeforeMatch[0].length - 1); // Keep the [
                continue;
            }

            // Opening bracket [#
            if (remaining.startsWith('[#')) {
                parts.push(<span key={key++} style={{ color: colors.bracket }}>[</span>);
                parts.push(<span key={key++} style={{ color: colors.delimiter }}>#</span>);
                remaining = remaining.slice(2);

                // Account number (until |)
                const pipeIdx = remaining.indexOf('|');
                if (pipeIdx > -1) {
                    parts.push(<span key={key++} style={{ color: colors.account }}>{remaining.slice(0, pipeIdx)}</span>);
                    parts.push(<span key={key++} style={{ color: colors.delimiter }}>|</span>);
                    remaining = remaining.slice(pipeIdx + 1);

                    // Alarm code (until ^)
                    const caretIdx = remaining.indexOf('^');
                    if (caretIdx > -1) {
                        parts.push(
                            <Tooltip key={key++} title="Code alarme SIA" arrow placement="top">
                                <span style={{ color: colors.code, fontWeight: 'bold' }}>{remaining.slice(0, caretIdx)}</span>
                            </Tooltip>
                        );
                        parts.push(<span key={key++} style={{ color: colors.delimiter }}>^</span>);
                        remaining = remaining.slice(caretIdx + 1);
                    }
                }
                continue;
            }

            // Closing ^]
            if (remaining.startsWith('^]')) {
                parts.push(<span key={key++} style={{ color: colors.delimiter }}>^</span>);
                parts.push(<span key={key++} style={{ color: colors.bracket }}>]</span>);
                remaining = remaining.slice(2);
                continue;
            }

            // Timestamp: _HH:MM:SS,MM-DD-YYYY
            const tsMatch = remaining.match(/^_(\d{2}:\d{2}:\d{2},\d{2}-\d{2}-\d{4})/);
            if (tsMatch) {
                parts.push(
                    <Tooltip key={key++} title="Horodatage" arrow placement="top">
                        <span style={{ color: colors.timestamp }}>_{tsMatch[1]}</span>
                    </Tooltip>
                );
                remaining = remaining.slice(tsMatch[0].length);
                continue;
            }

            // GPS coordinates: GPS{lat}/{lon}
            const gpsMatch = remaining.match(/^GPS(-?\d+\.?\d*)\/(-?\d+\.?\d*)/);
            if (gpsMatch) {
                parts.push(<span key={key++} style={{ color: colors.prefix }}>GPS</span>);
                parts.push(<span key={key++} style={{ color: colors.gps }}>{gpsMatch[1]}</span>);
                parts.push(<span key={key++} style={{ color: colors.value }}>/</span>);
                parts.push(<span key={key++} style={{ color: colors.gps }}>{gpsMatch[2]}</span>);
                remaining = remaining.slice(gpsMatch[0].length);
                continue;
            }

            // Key=Value pairs: #KEY=value (handle URLs with :// specially)
            const kvMatch = remaining.match(/^#([A-Z]+)=([^#\^]*)/);
            if (kvMatch) {
                parts.push(<span key={key++} style={{ color: colors.prefix }}>#{kvMatch[1]}=</span>);
                parts.push(<span key={key++} style={{ color: colors.value }}>{kvMatch[2]}</span>);
                remaining = remaining.slice(kvMatch[0].length);
                continue;
            }

            // Default: just add the character
            parts.push(<span key={key++} style={{ color: colors.value }}>{remaining[0]}</span>);
            remaining = remaining.slice(1);
        }

        return parts;
    };

    return (
        <Box
            component="pre"
            sx={{
                m: 0,
                p: 2,
                bgcolor: '#282c34',
                color: '#abb2bf',
                fontFamily: 'monospace',
                fontSize: '13px',
                minHeight: '100px',
                maxHeight: '400px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            }}
        >
            <code>{renderSia(content)}</code>
        </Box>
    );
};

interface MessagePreviewProps {
    config: TeleSurveilleurConfig | null;
    debounceMs?: number;
}

interface PreviewResponse {
    preview: string;
    format: string;
}

// Custom hook for debounced value
function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export const MessagePreview: React.FC<MessagePreviewProps> = ({
    config,
    debounceMs = 500,
}) => {
    const translate = useTranslate();
    const { open } = useNotification();
    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Debounce the config to prevent too many API calls
    const debouncedConfig = useDebouncedValue(config, debounceMs);

    const fetchPreview = useCallback(async (configToPreview: TeleSurveilleurConfig) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.post<PreviewResponse>('/company/preview-message', {
                teleSurveilleurConfig: configToPreview,
            });
            setPreview(response.data);
        } catch (err: any) {
            console.error('Error fetching preview:', err);
            setError(err?.response?.data?.message || 'Erreur lors de la génération de l\'aperçu');
            setPreview(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch preview when debounced config changes
    useEffect(() => {
        if (debouncedConfig) {
            fetchPreview(debouncedConfig);
        } else {
            setPreview(null);
        }
    }, [debouncedConfig, fetchPreview]);

    const handleRefresh = () => {
        if (config) {
            fetchPreview(config);
        }
    };

    const handleCopy = async () => {
        if (preview?.preview) {
            try {
                await navigator.clipboard.writeText(preview.preview);
                open?.({
                    type: 'success',
                    message: translate('telesurveilleur.preview.copied', 'Aperçu copié dans le presse-papier'),
                });
            } catch (err) {
                open?.({
                    type: 'error',
                    message: translate('telesurveilleur.preview.copyError', 'Erreur lors de la copie'),
                });
            }
        }
    };

    const getLanguage = (format: string): string => {
        switch (format) {
            case 'json':
                return 'json';
            case 'xml':
                return 'xml';
            default:
                return 'plaintext';
        }
    };

    if (!config) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 3,
                overflow: 'hidden',
                borderRadius: 2,
            }}
        >
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Code fontSize="small" />
                    <Typography variant="subtitle2">
                        {translate('telesurveilleur.preview.title', 'Aperçu du message')}
                    </Typography>
                    {isLoading && <CircularProgress size={16} />}
                </Stack>

                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={translate('telesurveilleur.preview.refresh', 'Rafraîchir')}>
                        <IconButton size="small" onClick={handleRefresh} disabled={isLoading}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('telesurveilleur.preview.copy', 'Copier')}>
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            disabled={!preview?.preview}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Content */}
            <Box sx={{ position: 'relative', minHeight: 100 }}>
                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {!error && preview?.preview && (
                    preview.format === 'text' ? (
                        <SiaHighlighter content={preview.preview} />
                    ) : (
                        <SyntaxHighlighter
                            language={getLanguage(preview.format)}
                            style={atomOneDark}
                            customStyle={{
                                margin: 0,
                                borderRadius: 0,
                                fontSize: '13px',
                                padding: '16px',
                                minHeight: '100px',
                                maxHeight: '400px',
                                overflow: 'auto',
                            }}
                            wrapLines
                            wrapLongLines
                        >
                            {preview.preview}
                        </SyntaxHighlighter>
                    )
                )}

                {!error && !preview?.preview && !isLoading && (
                    <Box
                        sx={{
                            p: 3,
                            textAlign: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <Typography variant="body2">
                            {translate(
                                'telesurveilleur.preview.empty',
                                'Configurez les options ci-dessus pour voir un aperçu'
                            )}
                        </Typography>
                    </Box>
                )}

                {isLoading && !preview && (
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>

        </Paper>
    );
};

export default MessagePreview;
